import { AsyncPipe, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet, RoutesRecognized } from '@angular/router';
import { AuthenticatedResult, OidcSecurityService } from 'angular-auth-oidc-client';
import { PrimeNGConfig, Translation } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { catchError, filter, mergeMap, Observable, of, take } from 'rxjs';
import { AppRoutes } from './app.app-manager-routes';
import { HeaderComponent } from './components/header/header.component';
import { MessageDataComponent } from './components/message-data/message-data.component';
import { CHUNK_RELOADED_KEY } from './interceptor/ChunkErrorHandler';
import { GenNotaAddComponent } from './modules/gestione/components/gen-nota-add/gen-nota-add.component';
import { EntityLogComponent } from './modules/gestione/components/genlog-list/entity-log.component';
import { ListExportComponent } from './modules/gestione/components/list-export/list-export.component';
import { AppInitialized, AppManagerService } from './services/app-manager.service';
import { ExternalNavigationService } from './services/external-navigation.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    EntityLogComponent,
    GenNotaAddComponent,
    HeaderComponent,
    ListExportComponent,
    MessageDataComponent,
    NgIf,
    RouterOutlet,
    ToastModule
  ]
})
//non ereditare da BaseUtenteComponent
export class AppComponent implements OnInit, OnDestroy {
  showOnlyPublic: boolean = false;

  isAuthenticated$: Observable<AuthenticatedResult>;

  appInitialized$: Observable<AppInitialized>;

  it: Translation = {
    firstDayOfWeek: 1,
    dayNames: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
    dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
    dayNamesMin: ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa'],
    monthNames: [
      'Gennaio',
      'Febbraio',
      'Marzo',
      'Aprile',
      'Maggio',
      'Giugno',
      'Luglio',
      'Agosto',
      'Settembre',
      'Ottobre',
      'Novembre',
      'Dicembre'
    ],
    monthNamesShort: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
    today: 'Oggi',
    clear: 'Cancella',
    dateFormat: 'dd/mm/yy',
    weekHeader: 'Settimane',
    emptyFilterMessage: 'Valore non trovato',
    emptyMessage: 'Nessun elemento',
    weak: 'Debole',
    medium: 'Media',
    strong: 'Forte',
    passwordPrompt: 'Inserire una password'
  };

  get isHiddenHeader(): boolean {
    return this.router.url.includes(AppRoutes.BrowserWindow.url);
  }

  constructor(
    private readonly oidcSecurityService: OidcSecurityService,
    private readonly appManagerService: AppManagerService,
    private readonly config: PrimeNGConfig,
    private readonly externalNavigation: ExternalNavigationService,
    public readonly router: Router
  ) {}

  ngOnDestroy(): void {
    this.appManagerService.dispose();
  }

  ngOnInit(): void {
    this.isAuthenticated$ = this.oidcSecurityService.isAuthenticated$;

    this.appInitialized$ = this.appManagerService.initialized$;

    this.router.events
      .pipe(
        filter((e): e is RoutesRecognized => e instanceof RoutesRecognized),
        mergeMap((event: RoutesRecognized) => {
          if (
            event.url.startsWith('/' + AppRoutes.AnteprimaAntidoping.url) ||
            event.url.startsWith('/' + AppRoutes.UnauthorizedUser.url)
          ) {
            this.showOnlyPublic = true;
            return of(undefined);
          }
          this.showOnlyPublic = false;
          return this.oidcSecurityService.checkAuth().pipe(
            filter((t) => t.isAuthenticated),
            catchError(() => of(undefined))
          );
        }),
        take(1)
      )
      .subscribe({
        next: (loginResponse) => {
          //puo' essere undefined
          if (loginResponse?.isAuthenticated) {
            this.appManagerService.initUser(loginResponse.userData, 'init');
            console.log('AppComponent - user authenticated');
          }
        }
      });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.externalNavigation.setExternalNavigation();
        // Navigazione riuscita: azzera la guardia anti-loop del reload da chunk mancante.
        sessionStorage.removeItem(CHUNK_RELOADED_KEY);
      }
    });

    this.config.setTranslation(this.it);
    // Viene settato per ovviare ad un bug #13216 di PrimeNg sulle multiselect che alla deselzione di un elemento chiude l'overlay
    this.config.overlayOptions.appendTo = 'body';

    window.onbeforeunload = () => this.ngOnDestroy();
  }
}
